import React, { useState, useRef, useEffect, useCallback } from "react";
import {
    Search,
    Plus,
    ChevronsUpDown,
    X,
    Check,
    Pencil,
    Trash2
} from "lucide-react";
import apiHelper from '../../apiHelper';

import { useHistory } from "react-router-dom";
import emptySearchImg from '../../assets/img/brand/empty-search.png';
import noBrandImage from '../../assets/img/brand/no-brand-img.png';
import { useLoader } from '../../components/GlobalLoader';
import DiscardConfirmationModal from "../../components/DiscardConfirmationModal";
import UnsavedChangesPrompt from "../../components/UnsavedChangesPrompt";
import AlertNotification from "../../components/AlertNotification";
import useSkuSchemeActions from "./useSkuSchemeActions";
import SortableHeader from "../../components/SortableHeader";
import DeleteConfirmModal from "../../components/DeleteConfirmModal";
import CustomPortal from "../../components/CustomPortal";
import PortalDropdown from "../../components/PortalDropdown";
import SearchBox from "../../components/SearchBox";

export default function SkuScheme() {
    const history = useHistory();

    const [abbrevColWidths, setAbbrevColWidths] = useState({
        variation: 180,
        option: 160,
        caseSensitive: 150,
        abbreviation: 150,
        remarks: 200,
    });

    const handleAbbrevColResizeMouseDown = (e, key, startWidth, minWidth = 100) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const onMouseMove = (moveEvent) => {
            const delta = moveEvent.clientX - startX;
            setAbbrevColWidths((prev) => ({ ...prev, [key]: Math.max(minWidth, startWidth + delta) }));
        };
        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };

    const AbbrevColResizeHandle = ({ colKey, minWidth }) => (
        <div
            className="col-resize-handle"
            onMouseDown={(e) => handleAbbrevColResizeMouseDown(e, colKey, abbrevColWidths[colKey], minWidth)}
            onClick={(e) => e.stopPropagation()}
            style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: '6px', cursor: 'col-resize', zIndex: 2, userSelect: 'none' }}
        />
    );

    const {
        activeTab,
        setActiveTab,
        breadcrumbs,
        dropdownOpenFor,
        setDropdownOpenFor,
        currentPage,
        setCurrentPage,
        cancelEditModalOpen,
        setCancelEditModalOpen,
        isDirty,
        setIsDirty,
        alertInfo,
        setAlertInfo,
        filterText,
        setFilterText,
        liveExample,
        setLiveExample,
        editRefs,
        editingIds,
        setEditingIds,
        cancelTargetId,
        setCancelTargetId,
        itemsPerPage,
        sortedFiltered,
        paginatedData,
        cursorPos,
        setCursorPos,
        errors,
        setErrors,

        // data
        pageData,
        setPageData,
        inputSchemes,
        setInputSchemes,
        data,
        setData,
        editRow,
        setEditRow,

        // helpers & renderers
        getPlaceholderOptions,
        placeholderOptions,
        parsePlaceholders,
        getSelectedPlaceholders,
        generateExample,
        renderInlineScheme,
        tagStyle,
        examplePartStyle,

        // actions
        startEdit,
        handleCancelEditConfirm,
        handleCancelEditClose,
        saveEdit,
        insertPlaceholder,
        handleSkuChange,
        handleKeyDown,
        handleBackspaceRemove,
        removePlaceholder,
        validateGenerationRule,
        handleKey,
        getCaretCharacterOffsetWithin, editValue,
        setEditValue,
        openDropdown,
        filter,
        setFilter,
        inputRefs,
        upwardDropdownId, setUpwardDropdownId,
        dropdownPosition,
        setDropdownPosition,
        renderColoredExample,
        alert,
        setAlert,
        showItemAlert,
        setShowItemAlert,


    } = useSkuSchemeActions();


    const [editingAbbreviationId, setEditingAbbreviationId] = useState([]);
    // const inputRefs = useRef({});

    const stripBraces = (ph) => ph.replace(/^\{|\}$/g, "");


    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [skuAttributes, setSkuAttributes] = useState([]);
    const [skuVariationOptions, setSkuVariationOptions] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const { showLoader, hideLoader } = useLoader();
    const [fieldErrors, setFieldErrors] = useState({});
    const [showToast, setShowToast] = useState(false);
    const [toastData, setToastData] = useState({ type: 'success', recordName: 'SKU Preset Option', action: 'update' });
    const [searchQuery, setSearchQuery] = useState("");
    const [submittedSearch, setSubmittedSearch] = useState("");
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showAbbreviationDiscardModal, setShowAbbreviationDiscardModal] = useState(false);
    const [cancelAbbreviationID, setCancelAbbreviationID] = useState(null);
    const [cancelAbbreviationType, setCancelAbbreviationType] = useState(null);
    const [pendingNavigationPath, setPendingNavigationPath] = useState(null);
    const [showExitModal, setShowExitModal] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editValues, setEditValues] = useState({});
    const startIndex = (currentPage - 1) * itemsPerPage + 1;
    const endIndex = Math.min(currentPage * itemsPerPage, sortedFiltered.length);
    const [defaultSorted, setDefaultSorted] = useState(false);
    const [presetDropdownOpenFor, setPresetDropdownOpenFor] = useState(null);
    const [presetDropdownPos, setPresetDropdownPos] = useState({});
    const skuOptionRefs = useRef({});

    // FIX 1: Add state to track if delete operation is in progress
    const [isDeleteOperation, setIsDeleteOperation] = useState(false);


    const startEditAbbrevation = (row) => {
        console.log(row);
        console.log(abbreviationsData);
        // setEditingAbbreviationId(row.sku_preset_option_id);
        setEditingAbbreviationId((prev) => {
            if (prev.includes(row.sku_preset_option_id)) return prev; 
            return [...prev, row.sku_preset_option_id];
        });
        setIsDirty(true); // ✅ mark dirty when edit starts

        let option = skuAttributes.filter((f) => f.sku_preset_id == row.sku_preset_id);
        console.log(option);
        // let id = option[0]['sku_preset_option_id'];
        let id = row.sku_preset_option_id;

        setSkuVariationOptions((prev) => ({
            ...prev,
            [id]: { ...(prev[id] || {}), options: option[0]['options'] }
        }));
    };


    const [editingRows, setEditingRows] = useState([]); // temporary new rows


    const handleAddClick = () => {
        const tempId = `temp-${Date.now()}`; // unique id for temporary rows
        setEditingRows([
            { id: tempId, sku_preset_id: "", variationOption: "", is_case_sensitive: "no", sku_preset_option_abbreviation: "", remarks: "", sku_preset_option_id: "" },
            ...editingRows,
        ]);
    };

    const handleChange = (id, field, value, type) => {
        console.log(type);
        // Load variations when SKU is selected
        if (field === "sku_preset_id") {
            const selectedOptions = skuAttributes
                .find(attr => attr.sku_preset_id == value)?.options || [];

            setSkuVariationOptions(prev => ({
                ...prev,
                [id]: { options: selectedOptions }
            }));
            setAbbreviationsData(prev =>
                prev.map(row =>
                    row.id == id ? { ...row, sku_preset_option_id: '' } : row
                )
            );
        }
        if (field === "sku_preset_option_id") {

            // duplicate validation rule
            const duplicateExists = originalAbbreviationsData.find(r => (r.id !== id  && r.sku_preset_option_id == value && r.sku_preset_option_abbreviation != ''));
            const duplicateErrors = {};
            if (duplicateExists) {
                if (duplicateExists.sku_preset_option_id != id) {
                    // duplicateErrors.sku_preset_option_id = `SKU Variation Option ${duplicateExists.sku_preset_option_name} is duplicated`;
                    duplicateErrors.sku_preset_option_id = 'Variation Option already exists';
                }
            } else {
                duplicateErrors.sku_preset_option_id = '';
            }
            setFieldErrors(prev => ({
                ...prev,
                [id]: duplicateErrors
            }));
            // if(duplicateErrors.sku_preset_option_id) return;
        }
        if (type && type === 'edit') {
            console.log(id);
            console.log(abbreviationsData);

            setAbbreviationsData(prev =>
                prev.map(row =>
                    row.id == id ? { ...row, [field]: value } : row
                )
            );
        } else {
            console.log(id);
            setEditingRows((prev) =>
                prev.map((row) =>
                    row.id === id ? { ...row, [field]: value } : row
                )
            );
        }
        setHasUnsavedChanges(true);

    };


    const handleSave = async (pkID, type) => {
    let currentRow;
    if (type && type === 'edit') {
        currentRow = abbreviationsData.find((r) => r.id === pkID);
    } else {
        currentRow = editingRows.find((r) => r.id === pkID);
    }

    const { id, sku_preset_id, variationOption, sku_preset_option_id, ...rowToSave } = currentRow;

    // Validate the specific row
    const validationErrors = validateAbbreviation(currentRow, !!editingId);

    if (Object.keys(validationErrors).length > 0) {
        setFieldErrors(prev => ({
            ...prev,
            [currentRow.id]: validationErrors
        }));
        setToastData({
            type: 'error',
            recordName: 'Abbreviation',
            action: 'create'
        });
        setShowToast(true);

        return;
    }

    try {
        const endpoint = `settings/sku-attribute-abbreviation/update/${sku_preset_option_id}/`;
        const method = 'POST';
        const response = await apiHelper(endpoint, method, rowToSave);

        if (response.ok) {
            const data = await response.json();
            
            // FIX: Only remove the saved row from editingRows, not filter by sku_preset_option_id
            // This was the bug - it was removing all rows with matching sku_preset_option_id
            setEditingRows(prev => prev.filter(item => item.id !== pkID));
            
            if (editingAbbreviationId) {
                setEditingAbbreviationId((prev) => prev.filter((x) => x !== id));
            }
            data.id = data.sku_preset_option_id;

            let updatedAbbreviationData;
            const exists = abbreviationsData.find(r => r.sku_preset_option_id == data.sku_preset_option_id);
            if (exists) {
                // update
                updatedAbbreviationData = abbreviationsData.map(r =>
                    r.sku_preset_option_id == data.sku_preset_option_id ? { ...r, ...data } : r
                );
            } else {
                // add new
                updatedAbbreviationData = [data, ...abbreviationsData];
            }

            if (type === 'edit') {
                if (data.sku_preset_option_name != rowToSave.sku_preset_option_name) {
                    updatedAbbreviationData = updatedAbbreviationData.filter((r) => r.sku_preset_option_name != rowToSave.sku_preset_option_name);
                }
            }

            setAbbreviationsData(updatedAbbreviationData);
            setOriginalAbbreviationsData(updatedAbbreviationData);

            // Show success toast
            setToastData({
                type: 'success',
                recordName: 'Abbreviation',
                action: sku_preset_option_id ? 'update' : 'create'
            });
            setShowToast(true);

            setIsDirty(false);

        } else {
            const error = await response.json();
            console.error("Save error:", error);
            setToastData({
                type: 'error',
                recordName: 'Abbreviation',
                action: editingId ? 'update' : 'create'
            });
            setShowToast(true);
        }
    } catch (error) {
        console.error("Save error:", error);
        setToastData({
            type: 'error',
            recordName: 'Abbreviation',
            action: editingId ? 'update' : 'create'
        });
        setShowToast(true);
    }
};


    // Validate single preset
    const validateAbbreviation = (abbreviationRow, isEditing = false) => {
        console.log(abbreviationRow);

        const duplicateErrors = {};

        if (!abbreviationRow.sku_preset_id) {
            duplicateErrors.sku_preset_id = 'SKU Variation is required';
        }

        if (!abbreviationRow.sku_preset_option_id) {
            duplicateErrors.sku_preset_option_id = 'Variation Option is required';
        }

        if (!abbreviationRow.sku_preset_option_abbreviation.trim()) {
            duplicateErrors.sku_preset_option_abbreviation = 'Abbreviation is required';
        }

        // duplicate validation rule
        const duplicateExists = originalAbbreviationsData.find(r => (r.id !== abbreviationRow.id && r.sku_preset_option_id == abbreviationRow.sku_preset_option_id && r.sku_preset_option_abbreviation != ''));
        if (duplicateExists) {
            if (duplicateExists.sku_preset_option_id != abbreviationRow.id) {
                // duplicateErrors.sku_preset_option_id = `SKU Variation Option ${duplicateExists.sku_preset_option} is duplicated`;
                duplicateErrors.sku_preset_option_id = 'Variation Option already exists';
            }
        }

        console.log(duplicateErrors);

        return duplicateErrors;
    };

    const handleCancel = (id, type) => {
        if (type === 'edit') {
            setCancelAbbreviationID(id);
            setCancelAbbreviationType(type);
            setShowAbbreviationDiscardModal(true);
            setIsDirty(true);
        } else {
            const cancelRow = editingRows.find((r) => r.id == id);
            setCancelAbbreviationID(id);
            setCancelAbbreviationType(type);
            if (cancelRow) {
                if (cancelRow.sku_preset_id || cancelRow.sku_preset_option_id || cancelRow.sku_preset_option_abbreviation.trim() || cancelRow.remarks.trim()) {
                    setShowAbbreviationDiscardModal(true);
                } else {
                    proceedCancelAbbreviation(id, 'new');
                }
            }
        }
    };

    const proceedCancelAbbreviation = (id, type = null) => {
        setShowAbbreviationDiscardModal(false);
        const checkType = type ?? cancelAbbreviationType;
        if (checkType === 'edit') {
            // setEditingAbbreviationId(null);
            setEditingAbbreviationId((prev) => prev.filter((x) => x !== id));
            // setAbbreviationsData(originalAbbreviationsData);
            const revertRow = originalAbbreviationsData.find((r) => r.id == id);
            setAbbreviationsData(prev =>
                prev.map(row =>
                    row.id == id ? revertRow : row
                )
            );

            setFieldErrors(prev => ({
                ...prev,
                [id]: {}
            }));
        } else {
            setEditingRows(editingRows.filter((r) => r.id !== id));
        }
    }

    const [abbreviationsData, setAbbreviationsData] = useState([]);
    const [originalAbbreviationsData, setOriginalAbbreviationsData] = useState([]);

    // Fetch All Variations on Load
    const fetchVariations = async () => {
        showLoader()
        try {
            const response = await apiHelper('settings/sku-attributes/', 'GET');
            // apiHelper resolves to undefined when the request never completes,
            // so guard before touching .json() — otherwise the page white-screens.
            if (!response || !response.ok) throw new Error('Fetch failed');
            const data = await response.json();
            const list = Array.isArray(data) ? data : [];
            const sortedData = [...list].sort((a, b) =>
                (a.sku_preset_name || '').localeCompare(b.sku_preset_name || '')
            );
            setSkuAttributes(sortedData);
        } catch (error) {
            console.error('Error fetching SKU variations:', error);
            setSkuAttributes([]);
            setAlertInfo({ type: 'error', recordName: 'SKU Variation', action: 'fetch' });
        } finally {
            hideLoader()
        }
    };
    // Fetch All Abbreviations on Load
    const fetchAbbreviations = async () => {
        showLoader()
        try {
            const response = await apiHelper('settings/sku-attributes-abbrevations/?is_abbreviated=true', 'GET');
            if (!response || !response.ok) throw new Error('Fetch failed');
            const data = await response.json();
            const list = (Array.isArray(data) ? data : []).map(r => ({
                ...r,
                id: r.sku_preset_option_id
            }));
            setAbbreviationsData(list);
            setOriginalAbbreviationsData(list);
        } catch (error) {
            console.error('Error fetching SKU abbreviations:', error);
            setAbbreviationsData([]);
            setOriginalAbbreviationsData([]);
            setAlertInfo({ type: 'error', recordName: 'Abbreviation', action: 'fetch' });
        } finally {
            hideLoader()
        }
    };

    useEffect(() => {
        fetchVariations();
        fetchAbbreviations();
    }, []);

    // FIX 2: Updated navigation blocking to exclude delete operations
    useEffect(() => {
        const unblock = history.block((tx) => {
            console.log(tx);
            
            // Don't block navigation if delete operation is in progress
            if (isDeleteOperation) {
                console.log("Delete in progress, allowing navigation");
                return true;
            }
            
            // Only block if there are actual unsaved changes
            if (hasUnsavedChanges || isDirty) {
                // Handle different react-router versions
                let pathname;
                if (typeof tx === 'string') {
                    // react-router v5 and below
                    pathname = tx;
                } else if (tx.pathname) {
                    // react-router v6
                    pathname = tx.pathname;
                } else if (tx.location) {
                    // react-router v5 with location object
                    pathname = tx.location.pathname;
                } else {
                    // Fallback
                    pathname = window.location.pathname;
                }

                // Check if we're actually navigating to a different module
                // This prevents the modal from showing when just closing modals
                const isSameModule = pathname.includes('/sku-scheme') || 
                                    pathname.includes(window.location.pathname);
                
                if (!isSameModule) {
                    setPendingNavigationPath(pathname);
                    setShowExitModal(true);
                    return false; // Block navigation
                }
            }
            return true; // Allow navigation
        });

        return () => unblock();
    }, [hasUnsavedChanges, isDirty, isDeleteOperation, history]);

    // Cleanup navigation state on unmount
    useEffect(() => {
        return () => {
            setPendingNavigationPath(null);
            setShowExitModal(false);
        };
    }, []);

    // FIXED: Handle navigation confirmation
    const confirmNavigation = () => {
        // Clear all unsaved changes
        setFieldErrors({});
        setIsDirty(false);
        setHasUnsavedChanges(false);
        setIsDeleteOperation(false); // Reset delete flag

        // Close the modal
        setShowExitModal(false);

        // Use setTimeout to ensure state updates are processed before navigation
        setTimeout(() => {
            if (pendingNavigationPath) {
                history.push(pendingNavigationPath);
            }
            setPendingNavigationPath(null);
        }, 0);
    };

    // FIXED: Handle navigation cancellation
    const cancelNavigation = () => {
        setShowExitModal(false);
        setPendingNavigationPath(null);
        setIsDeleteOperation(false); // Reset delete flag
    };

    useEffect(() => {
        if (!defaultSorted && abbreviationsData.length > 0 && skuAttributes.length > 0) {
            handleSort('sku_preset_id');
            setDefaultSorted(true);
        }
    }, [skuAttributes, abbreviationsData, defaultSorted]);

    const showTable = (data.length > 0 || editingRows.length > 0);


    useEffect(() => {
        const savedData = localStorage.getItem("sku-generation-rules");
        if (savedData) {
            setPageData(JSON.parse(savedData));
        }
    }, []);
    useEffect(() => {
        localStorage.setItem("sku-generation-rules", JSON.stringify(pageData));
    }, [pageData]);


    useEffect(() => {
        const onBeforeUnload = (e) => {
            if (isDirty && !isDeleteOperation) {
                e.preventDefault();
                e.returnValue = ''; // Chrome requires returnValue to be set
            }
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [isDirty, isDeleteOperation]);

    // FIX 3: Updated handleDeleteRow to manage delete operation state
    const handleDeleteRow = async (sku_preset_option_id) => {
        console.log("Deleting row with id:", sku_preset_option_id);
        
        // Mark delete operation as in progress
        setIsDeleteOperation(true);

        const response = await apiHelper(`settings/sku-attribute-abbreviation/${sku_preset_option_id}/delete/`, 'DELETE');

        if (response.ok) {
            setAbbreviationsData(prev =>
                prev.filter(item => item.sku_preset_option_id !== sku_preset_option_id)
            );
            setOriginalAbbreviationsData(prev =>
                prev.filter(item => item.sku_preset_option_id !== sku_preset_option_id)
            );
            
            // Reset delete flag after successful deletion
            setTimeout(() => {
                setIsDeleteOperation(false);
            }, 100);
        } else {
            const error = await response.json();
            console.error("Delete error:", error);
            setToastData({
                type: 'error',
                recordName: 'Abbreviation',
                action: 'delete'
            });
            setShowToast(true);
            
            // Reset delete flag on error
            setIsDeleteOperation(false);
        }

    };

    const [sortField, setSortField] = useState("");
    const [sortOrder, setSortOrder] = useState("asc");

    const handleSort = (field) => {
  let order = "asc";

  if (sortField === field && sortOrder === "asc") {
    order = "desc";
  }

  setSortField(field);
  setSortOrder(order);
  
  let orderData = [...abbreviationsData].sort((a, b) => {
    let valA = a[field] ?? "";
    let valB = b[field] ?? "";

    // For sku_preset_id field, get the variation name
    if (field === 'sku_preset_id') {
      valA = skuAttributes.find((r) => r.sku_preset_id == valA)?.sku_preset_name?.toLowerCase() || "";
      valB = skuAttributes.find((r) => r.sku_preset_id == valB)?.sku_preset_name?.toLowerCase() || "";
    } else {
      // Convert to lowercase for case-insensitive comparison
      valA = String(valA).toLowerCase();
      valB = String(valB).toLowerCase();
    }

    // Handle empty/null values
    if (valA === "" && valB !== "") return order === "asc" ? -1 : 1;
    if (valB === "" && valA !== "") return order === "asc" ? 1 : -1;
    if (valA === "" && valB === "") return 0;

    

    // Perform comparison
    if (order === "asc") {
      return valA.localeCompare(valB, undefined, { sensitivity: 'base' });
    } else {
      return valB.localeCompare(valA, undefined, { sensitivity: 'base' });
    }
  });

  setAbbreviationsData(orderData);
  setOriginalAbbreviationsData(orderData);
};

    // Search must stay enabled whenever the user is actively searching, even if
    // that search yields zero results — otherwise they cannot clear it. (ERP-5005)
    const isAbbrevSearching = submittedSearch.trim().length > 0;
    const isAbbrevTableEmpty =
        abbreviationsData.length === 0 && editingRows.length === 0 && !isAbbrevSearching;

    const filteredAbbreviations = abbreviationsData.filter((item) =>
        skuAttributes.find((r) => r.sku_preset_id == item.sku_preset_id)?.sku_preset_name?.toLowerCase().includes(submittedSearch.toLowerCase()) ||
        item.sku_preset_option_name?.toLowerCase().includes(submittedSearch.toLowerCase()) ||
        item.is_case_sensitive?.toLowerCase().includes(submittedSearch.toLowerCase()) ||
        item.sku_preset_option_abbreviation?.toLowerCase().includes(submittedSearch.toLowerCase()) ||
        item.remarks?.toLowerCase().includes(submittedSearch.toLowerCase())
    );

    // FIX 4: Updated unsaved changes tracking to exclude delete operations
    useEffect(() => {
        // Skip checking during delete operations
        if (isDeleteOperation) {
            return;
        }
        
        let ifChanged = false;
        abbreviationsData.forEach((abb, ind) => {
            if (!ifChanged)
                ifChanged = !(JSON.stringify(abb) === JSON.stringify(originalAbbreviationsData[ind]));
        });
        const hasChanges = (ifChanged || editingRows.length > 0);
        setHasUnsavedChanges(hasChanges);
        setIsDirty(hasChanges);
    }, [abbreviationsData, editingRows, originalAbbreviationsData, isDeleteOperation]);

    const normalize = (str = "") => {
        return str
            .replace(/[{}]/g, "")   // remove { }
            .replace(/_/g, "")      // remove _
            .replace(/\s+/g, "")    // remove spaces (optional but helpful)
            .toLowerCase();         // normalize case
    };

    // FIX 5: Delete modal handlers
    const handleDeleteConfirm = () => {
        // Mark delete operation as in progress
        setIsDeleteOperation(true);
        
        // Perform the delete
        handleDeleteRow(deleteId);
        
        // Close modal and reset delete ID
        setShowDeleteModal(false);
        setDeleteId(null);
    };

    const handleDeleteCancel = () => {
        // Reset delete operation flag
        setIsDeleteOperation(false);
        
        // Close modal and reset delete ID
        setShowDeleteModal(false);
        setDeleteId(null);
    };

    return (
        <div className="company-body new-look">
            {/* Breadcrumb */}
            <div className="breadcrumb">
                {breadcrumbs.map((crumb, index) => (
                    <span key={index} className="breadcrumb-item">
                        {crumb}
                        {index !== breadcrumbs.length - 1}
                    </span>
                ))}
                {alertInfo && (
                    <AlertNotification
                        type={alertInfo.type}
                        recordName={alertInfo.recordName}
                        action={alertInfo.action}
                        onClose={() => setAlertInfo(null)}
                    />
                )}

            </div>

            <div className="employee-tabs-container">
                {/* Tabs */}
                <div className="tabs">
                    {["Generation", "Abbreviation Dictionary"].map((tab) => (
                        <button
                            key={tab}
                            className={`tab ${activeTab === tab ? "active" : ""}`}
                            onClick={() => setActiveTab(tab)}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                {activeTab === "Generation" && (
                    <div className="tab-content">
                        <div className="info-section">
                            <div className="info-card">
                                {/* Header */}
                                <div className="info-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h4 className="info-title">Generation Rule</h4>
                                </div>

                                <>
                                    <div className="generation-rules">
                                        {/* Header Text Section */}
                                        <div
                                            style={{
                                                display: "flex",
                                                width: "100%",
                                                padding: "15px 20px",
                                                alignItems: "center",
                                                background: "#FFF",
                                                //   borderBottom: "1px solid #E9ECEF",
                                            }}
                                        >
                                            <p
                                                style={{
                                                    fontFamily: "Montserrat",
                                                    fontSize: "14px",
                                                    fontStyle: "normal",
                                                    fontWeight: 400,
                                                    lineHeight: "20px",
                                                    color: "#1F1F1F",
                                                }}
                                            >
                                                The following placeholders are interchangeable building blocks to
                                                construct your SKU scheme:
                                            </p>
                                        </div>

                                        {/* Placeholder Rows */}
                                        <div
                                            style={{
                                                padding: "15px 20px",
                                                fontFamily: "Montserrat",
                                                fontSize: "14px",
                                                fontStyle: "normal",
                                                fontWeight: 400,
                                                lineHeight: "20px",
                                                color: "#1F1F1F",
                                                display: "flex",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <div>
                                                <span style={{ color: "#2C7BE5" }}>
                                                    {"{BrandPrefix}"}
                                                </span>{" "}
                                                = Prefix of Brand
                                            </div>

                                            <div>
                                                <span style={{ color: "#FF00BF" }}>
                                                    {"{CategoryPrefix}"}
                                                </span>{" "}
                                                = Prefix of the last level Category
                                            </div>

                                            <div>
                                                <span style={{ color: "#00FF37" }}>
                                                    {"{SPUPrefix}"}
                                                </span>{" "}
                                                = Prefix of SPU
                                            </div>

                                            <div>
                                                <span style={{ color: "#FF8000" }}>
                                                    {"{SKUAbbr}"}
                                                </span>{" "}
                                                = Composed of SKU Variations' abbreviations
                                            </div>
                                        </div>

                                        {/* Example Section */}
                                        <div
                                            style={{
                                                padding: "15px 20px",
                                                fontFamily: "Montserrat",
                                                fontSize: "14px",
                                                fontStyle: "normal",
                                                fontWeight: 400,
                                                lineHeight: "20px",
                                                color: "#5E6E82",
                                                // paddingTop: '15px'
                                            }}
                                        >
                                            An example of{" "}
                                            <span style={{ color: "#2C7BE5" }}>
                                                {"{BrandPrefix}"}
                                            </span>
                                            <span style={{ color: "#FF00BF" }}>
                                                {"{CategoryPrefix}"}
                                            </span>
                                            <span style={{ color: "#00FF37" }}>
                                                {"{SPUPrefix}"}
                                            </span>
                                            <span style={{ color: "#FF8000" }}>
                                                {"{SKUAbbr}"}
                                            </span>{" "}
                                            :
                                        </div>

                                        {/* Example Box */}
                                        <div
                                            style={{
                                                display: 'flex',
                                                width: '1064px',
                                                padding: '15px 20px',
                                                background: '#FFF',
                                                alignItems: 'center',
                                                gap: '10px'
                                            }}
                                        >
                                            <div style={{
                                                alignSelf: 'stretch', display: 'flex',
                                                width: '1064px',
                                                padding: '16px 12px',
                                                borderRadius: '12px',
                                                background: '#F9FAFD',
                                            }}>
                                                <span style={{
                                                    color: "#2C7BE5", fontFamily: "Montserrat",
                                                    fontSize: "14px",
                                                    fontStyle: "normal",
                                                    fontWeight: 500,
                                                    lineHeight: "20px",
                                                    letterSpacing: "0.267px",
                                                }}>APPL</span>
                                                <span style={{
                                                    color: "#FF00BF", fontFamily: "Montserrat",
                                                    fontSize: "14px",
                                                    fontStyle: "normal",
                                                    fontWeight: 500,
                                                    lineHeight: "20px",
                                                    letterSpacing: "0.267px",
                                                }}>PHN</span>
                                                <span style={{
                                                    color: "#00FF37", fontFamily: "Montserrat",
                                                    fontSize: "14px",
                                                    fontStyle: "normal",
                                                    fontWeight: 500,
                                                    lineHeight: "20px",
                                                    letterSpacing: "0.267px",
                                                }}>IP6PM</span>
                                                <span style={{
                                                    color: "#FF8000", fontFamily: "Montserrat",
                                                    fontSize: "14px",
                                                    fontStyle: "normal",
                                                    fontWeight: 500,
                                                    lineHeight: "20px",
                                                    letterSpacing: "0.267px",
                                                }}>BLK256GBWIFI</span>
                                            </div>
                                        </div>
                                    </div>
                                </>

                                {/* Table */}
                                <div className="entities-tab">
                                    <div className="table-wrapper">
                                        <table className="entities-table" style={{
                                            tableLayout: "fixed",
                                            width: "100%"
                                        }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ height: '45px', width: "15%" }}>
                                                        Type
                                                    </th>
                                                    <th style={{ height: '45px', width: "40%" }} >
                                                        Generation Rule
                                                    </th>
                                                    <th style={{ height: '45px', textAlign: 'center' }}>Action</th>
                                                    <th style={{ textAlign: 'left', height: '45px', width: "35%" }}>Example</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedData.map((row, idx) => (
                                                    <tr key={row.sku_code_generation_id}
                                                        onClick={() => console.log("Row clicked", row)}
                                                        className={[idx % 2 ? 'striped' : ''].join(' ')}
                                                        style={{
                                                            height: '45px', whiteSpace: "normal",
                                                            wordBreak: "break-word"
                                                        }}
                                                    >
                                                        {/* Product Type */}
                                                        <td>
                                                            <span className="product-type-badge">
                                                                {row.product_type}
                                                            </span>
                                                        </td>

                                                        {/* Scheme Column */}
                                                        <td style={{ position: "relative" }}>
                                                            {editingIds.has(row.sku_code_generation_id) ? (
                                                                <div style={{ position: "relative" }}>
                                                                    <input
                                                                        ref={el => (inputRefs.current[row.sku_code_generation_id] = el)}
                                                                        className="form-control"
                                                                        value={editValue[row.sku_code_generation_id] ?? row.scheme}
                                                                        onChange={e => {
                                                                            const val = e.target.value;
                                                                            setEditValue(prev => ({
                                                                                ...prev,
                                                                                [row.sku_code_generation_id]: val,
                                                                            }));
                                                                            // remove error once user types something
                                                                            if (val.trim()) {
                                                                                setErrors(prev => {
                                                                                    const newErrors = { ...prev };
                                                                                    delete newErrors[row.sku_code_generation_id];
                                                                                    return newErrors;
                                                                                });
                                                                            }
                                                                        }}
                                                                        
                                                                        onKeyDown={(e) => handleKey(e, row.sku_code_generation_id)}
                                                                        onBlur={() => {
                                                                            const visibleValue = editValue[row.sku_code_generation_id] ?? row.scheme;

                                                                            if (!visibleValue?.trim()) {
                                                                                setErrors(prev => ({
                                                                                    ...prev,
                                                                                    [row.sku_code_generation_id]: "Generation Rule is required"
                                                                                }));
                                                                            } else {
                                                                                // Remove error if value is valid
                                                                                setErrors(prev => {
                                                                                    const next = { ...prev };
                                                                                    delete next[row.sku_code_generation_id];
                                                                                    return next;
                                                                                });
                                                                            }
                                                                        }}

                                                                        style={{
                                                                            border: errors[row.sku_code_generation_id]
                                                                                ? "1px solid #F00"   // 🔥 red border
                                                                                : "1px solid #666"
                                                                        }}

                                                                    />

                                                                    {/* Dropdown */}
                                                                    {dropdownOpenFor === row.sku_code_generation_id && (
                                                                        <CustomPortal>
                                                                            <div
                                                                                id={`dropdown-${row.sku_code_generation_id}`}
                                                                                className="placeholder-dropdown-fixed"
                                                                                style={{
                                                                                    position: "absolute",
                                                                                    top: dropdownPosition[row.sku_code_generation_id]?.top,
                                                                                    left: dropdownPosition[row.sku_code_generation_id]?.left,
                                                                                    width: dropdownPosition[row.sku_code_generation_id]?.width
                                                                                }}
                                                                            >
                                                                                {(() => {
                                                                                    const usedPlaceholders = parsePlaceholders(
                                                                                        editValue[row.sku_code_generation_id] ?? row.scheme
                                                                                    );

                                                                                    const filtered = placeholderOptions
                                                                                        .filter(opt => normalize(opt.label).includes(normalize(filter)));


                                                                                    if (filtered.length === 0) {
                                                                                        return (
                                                                                            <div
                                                                                                style={{
                                                                                                    padding: "10px 30px",
                                                                                                    color: "#999",
                                                                                                    fontFamily: "Montserrat",
                                                                                                    fontSize: "14px",
                                                                                                    textAlign: "left"
                                                                                                }}
                                                                                            >
                                                                                                No match found
                                                                                            </div>
                                                                                        );
                                                                                    }

                                                                                    return filtered.map(opt => (
                                                                                        <div
                                                                                            key={opt.label}
                                                                                            onClick={() => insertPlaceholder(opt.label, row.sku_code_generation_id)}
                                                                                            style={{
                                                                                                padding: "10px 30px",
                                                                                                cursor: "pointer",
                                                                                                color: opt.color,
                                                                                                fontFamily: "Montserrat",
                                                                                                fontSize: "14px",
                                                                                                fontWeight: "400",
                                                                                                lineHeight: "20px"
                                                                                            }}
                                                                                        >
                                                                                            {opt.label}
                                                                                        </div>
                                                                                    ));
                                                                                })()}
                                                                            </div>
                                                                        </CustomPortal>
                                                                    )}

                                                                </div>
                                                            ) : (
                                                                <div className="sku-scheme-text">{renderInlineScheme(row.scheme)}</div>
                                                            )}
                                                            {errors[row.sku_code_generation_id] && (
                                                                <div className="invalid-feedback" style={{
                                                                    display: 'block',
                                                                    fontSize: '12px',
                                                                    color: "#F00",
                                                                    fontFamily: "Montserrat",
                                                                    fontStyle: 'normal',
                                                                    fontWeight: '400px',
                                                                    lineHeight: 'normal',
                                                                    marginTop: '5px'
                                                                }}>
                                                                    {errors[row.sku_code_generation_id]}
                                                                </div>
                                                            )}
                                                        </td>

                                                        {/* Action Column */}
                                                        <td style={{
                                                            textAlign: "center",
                                                            verticalAlign: "middle"
                                                        }}
                                                        >
                                                            <div
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                }}
                                                            >
                                                                {editingIds.has(row.sku_code_generation_id)
                                                                    ? (
                                                                        <>
                                                                            <button
                                                                                className="icon-btn shadow-none text-gray"
                                                                                onClick={() => {
                                                                                    saveEdit(row.sku_code_generation_id);
                                                                                    setIsDirty(false);
                                                                                    setDropdownOpenFor(null);

                                                                                }}
                                                                            >
                                                                                <Check size={18} />
                                                                            </button>
                                                                            <button
                                                                                className="icon-btn shadow-none text-gray"
                                                                                onClick={() => {
                                                                                    setCancelEditModalOpen(true);
                                                                                    setDropdownOpenFor(null);
                                                                                    setCancelTargetId(row.sku_code_generation_id);
                                                                                }}
                                                                            >
                                                                                <X size={18} />
                                                                            </button>
                                                                        </>
                                                                    ) : (
                                                                        <button
                                                                            className="icon-btn shadow-none text-gray"
                                                                            onClick={() => startEdit(row)}
                                                                        >
                                                                            <Pencil size={18} />
                                                                        </button>
                                                                    )}
                                                            </div>
                                                        </td>

                                                        {/* Example Column */}
                                                        <td style={{ textAlign: "left" }}>
                                                            <div className="example-text">
                                                                {editingIds.has(row.sku_code_generation_id)

                                                                    ? renderColoredExample(
                                                                        editValue[row.sku_code_generation_id] ?? row.scheme,
                                                                        row.product_type
                                                                    )
                                                                    : renderColoredExample(row.scheme, row.product_type)
                                                                }
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>

                                        </table>
                                        <DiscardConfirmationModal
                                            isOpen={cancelEditModalOpen}
                                            onCancel={handleCancelEditClose}
                                            onConfirm={handleCancelEditConfirm}
                                            entityName="Generation Rule"
                                        />

                                    </div>
                                    <div className="pagination">
                                        {/* Info Text */}
                                        <div className="pagination-info" >
                                            {` ${startIndex} - ${endIndex} of ${sortedFiltered.length} `}
                                        </div>
                                    </div>
                                    <UnsavedChangesPrompt isDirty={isDirty} entityName="Generation Rule" />

                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Other Tab */}
                {activeTab !== "Abbreviation Dictionary" && (
                    <DiscardConfirmationModal
                        isOpen={showExitModal}
                        onCancel={cancelNavigation}
                        onConfirm={confirmNavigation}
                        entityName="SKU Scheme"
                        title="Unsaved Changes"
                        message="You have unsaved changes. Are you sure you want to leave?"
                        confirmText="Leave"
                        cancelText="Stay"
                    />
                )}

                {activeTab === "Abbreviation Dictionary" && (
                    <div className="tab-content">
                        <div className="info-section">
                            <div className="info-card">
                                {/* Table */}
                                <div className="entities-tab">
                                    <div className="toolbar">
                                        <div className="toolbar-left">
                                            <SearchBox
                                                searchInput={searchQuery}
                                                setSearchInput={setSearchQuery}
                                                setSearchTerm={setSubmittedSearch}
                                                setCurrentPage={() => { }}   // if you don't use pagination, keep empty
                                                placeholder="Search…"
                                                disabled={isAbbrevTableEmpty}
                                            />

                                        </div>
                                        <div className="toolbar-right">
                                            <button
                                                className="icon-btn dark-border"
                                                onClick={handleAddClick}
                                            >
                                                <Plus size={18} /> Add Abbreviations
                                            </button>
                                        </div>
                                    </div>

                                    {/* Toast Notification */}
                                    {showToast && (
                                        <AlertNotification
                                            type={toastData.type}
                                            recordName={toastData.recordName}
                                            action={toastData.action}
                                            onClose={() => setShowToast(false)}
                                        />
                                    )}

                                    {/* Discard Confirmation Modal */}
                                    <DiscardConfirmationModal
                                        isOpen={showAbbreviationDiscardModal}
                                        onCancel={() => {
                                            setShowAbbreviationDiscardModal(false);
                                            setCancelAbbreviationID(null);
                                            setCancelAbbreviationType(null);
                                        }}
                                        onConfirm={() => {
                                            proceedCancelAbbreviation(cancelAbbreviationID);
                                        }}
                                        entityName="SKU Abbreviation"
                                    />


                                    {/* Navigation Confirmation Modal */}
                                    <DiscardConfirmationModal
                                        isOpen={showExitModal}
                                        onCancel={cancelNavigation}
                                        onConfirm={confirmNavigation}
                                        entityName="SKU Abbreviation"
                                        title="Unsaved Changes"
                                        message="You have unsaved changes. Are you sure you want to leave?"
                                        confirmText="Leave"
                                        cancelText="Stay"
                                    />



                                    <div className="table-wrapper" style={{
                                        border: "none"

                                    }}>
                                        {abbreviationsData.length === 0 && editingRows.length === 0 ? (
                                            <div className="empty-state">
                                                <div className="empty-state-content">
                                                    <div class="icon"><img
                                                        src={noBrandImage}
                                                        alt="avatar"
                                                        className="avatar"
                                                        style={{
                                                            width: 120,
                                                            height: 120,
                                                            verticalAlign: 'middle'
                                                        }}
                                                    /></div>
                                                    <h1 class="no-item-title mt-2">
                                                        No Abbreviation Found
                                                    </h1>
                                                    <p class="no-item-text">
                                                        Nothing here yet. Add your first item to get started!
                                                    </p>

                                                </div>
                                            </div>
                                        ) :
                                            (
                                                /* 2. Search returns 0 results */
                                                filteredAbbreviations.length === 0 && editingRows.length === 0 ? (
                                                    <div className="empty-state">
                                                        <div className="empty-state-content">
                                                            <div class="icon"><img
                                                                src={emptySearchImg}
                                                                alt="avatar"
                                                                className="avatar"
                                                                style={{
                                                                    width: 120,
                                                                    height: 120,
                                                                    verticalAlign: 'middle'
                                                                }}
                                                            /></div>
                                                            <h1 className="no-item-title mt-2">No Abbreviation Found</h1>
                                                            <p className="no-item-text">
                                                                No results match what you're looking for
                                                            </p>
                                                        </div>
                                                    </div>
                                                ) :
                                                    (
                                                        <table className="entities-table" style={{ tableLayout: 'fixed' }}>
                                                            <thead height="45px">
                                                                <tr>
                                                                    <th style={{ width: 56, minWidth: 56, maxWidth: 56 }}></th>
                                                                    <SortableHeader
                                                                        label="SKU Variation"
                                                                        field="sku_preset_id"
                                                                        sortField={sortField}
                                                                        sortOrder={sortOrder}
                                                                        handleSort={handleSort}
                                                                        style={{ width: abbrevColWidths.variation, position: 'relative', overflow: 'hidden' }}
                                                                        resizeHandle={<AbbrevColResizeHandle colKey="variation" minWidth={120} />}
                                                                    />

                                                                    <SortableHeader
                                                                        label="Variation Option"
                                                                        field="sku_preset_option_name"
                                                                        sortField={sortField}
                                                                        sortOrder={sortOrder}
                                                                        handleSort={handleSort}
                                                                        style={{ width: abbrevColWidths.option, position: 'relative', overflow: 'hidden' }}
                                                                        resizeHandle={<AbbrevColResizeHandle colKey="option" minWidth={120} />}
                                                                    />

                                                                    <SortableHeader
                                                                        label="Case Sensitive"
                                                                        field="is_case_sensitive"
                                                                        sortField={sortField}
                                                                        sortOrder={sortOrder}
                                                                        handleSort={handleSort}
                                                                        style={{ width: abbrevColWidths.caseSensitive, position: 'relative', overflow: 'hidden' }}
                                                                        resizeHandle={<AbbrevColResizeHandle colKey="caseSensitive" minWidth={100} />}
                                                                    />

                                                                    <SortableHeader
                                                                        label="Abbreviation"
                                                                        field="sku_preset_option_abbreviation"
                                                                        sortField={sortField}
                                                                        sortOrder={sortOrder}
                                                                        handleSort={handleSort}
                                                                        style={{ width: abbrevColWidths.abbreviation, position: 'relative', overflow: 'hidden' }}
                                                                        resizeHandle={<AbbrevColResizeHandle colKey="abbreviation" minWidth={120} />}
                                                                    />

                                                                    <SortableHeader
                                                                        label="Remarks"
                                                                        field="remarks"
                                                                        sortField={sortField}
                                                                        sortOrder={sortOrder}
                                                                        handleSort={handleSort}
                                                                        style={{ width: abbrevColWidths.remarks, position: 'relative', overflow: 'hidden' }}
                                                                        resizeHandle={<AbbrevColResizeHandle colKey="remarks" minWidth={120} />}
                                                                    />


                                                                    <th style={{ textAlign: 'center', width: 90, minWidth: 90, maxWidth: 90 }}>Action</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {editingRows.map((row, idx) => {

                                                                    const rowHeight = 45;
                                                                    const rowBgColor = idx % 2 === 0 ? '#FFFFFF' : '#F8F9FA';

                                                                    return (
                                                                        <tr key={row.id} style={{ backgroundColor: rowBgColor }}>
                                                                            <td style={{ borderBottom: "none", verticalAlign: 'top' }}></td>
                                                                            {/* SKU Variation */}
                                                                            <td className={fieldErrors[row.id]?.sku_preset_id ? 'field-error' : ''} style={{ borderBottom: "none", verticalAlign: 'top' }}>

                                                                                <PortalDropdown
                                                                                    value={row.sku_preset_id}
                                                                                    options={skuAttributes.map(attr => ({
                                                                                        value: attr.sku_preset_id,
                                                                                        label: attr.sku_preset_name,
                                                                                    }))}
                                                                                    onChange={(val) =>
                                                                                        handleChange(row.id, "sku_preset_id", val)
                                                                                    }
                                                                                />

                                                                                {fieldErrors[row.id]?.sku_preset_id && (
                                                                                    <div style={{ color: '#F04438', fontSize: '12px', marginTop: '2px' }}>
                                                                                        {fieldErrors[row.id].sku_preset_id}
                                                                                    </div>
                                                                                )}
                                                                            </td>

                                                                            {/* Variation Option */}
                                                                            <td className={'bg_yellow_option ' + (fieldErrors[row.id]?.sku_preset_option_id ? ' field-error' : '')} style={{ borderBottom: "none", verticalAlign: 'top' }} >
                                                                                <PortalDropdown
                                                                                    value={row.sku_preset_option_id}
                                                                                    options={
                                                                                        skuVariationOptions[row.id]?.options.map(opt => ({
                                                                                            value: opt.sku_preset_option_id,
                                                                                            label: opt.sku_preset_option_name,
                                                                                        })) || []
                                                                                    }
                                                                                    onChange={(val) =>
                                                                                        handleChange(row.id, "sku_preset_option_id", val)
                                                                                    }
                                                                                    fieldName="variation_option"
                                                                                />

                                                                                {fieldErrors[row.id]?.sku_preset_option_id && (
                                                                                    <div style={{ color: '#F04438', fontSize: '12px', marginTop: '2px' }}>
                                                                                        {fieldErrors[row.id].sku_preset_option_id}
                                                                                    </div>
                                                                                )}
                                                                            </td>

                                                                            {/* Case Sensitive */}
                                                                            <td style={{ borderBottom: "none", verticalAlign: 'top' }} className="bg_yellow_option" data-text={row.is_case_sensitive}>
                                                                                <PortalDropdown
                                                                                    value={row.is_case_sensitive ?? 'no'}
                                                                                    options={
                                                                                        [
                                                                                            {'value': 'yes','label': 'YES'},
                                                                                            {'value': 'no','label': 'NO'},
                                                                                        ]
                                                                                    }
                                                                                    onChange={(val) =>
                                                                                        handleChange(row.id, "is_case_sensitive", val)
                                                                                    }
                                                                                />
                                                                            </td>

                                                                            {/* Abbreviation */}
                                                                            <td className={fieldErrors[row.id]?.sku_preset_option_abbreviation ? 'field-error' : ''} style={{ borderBottom: "none", verticalAlign: 'top' }}>
                                                                                <input
                                                                                    onChange={(e) =>
                                                                                        handleChange(row.id, "sku_preset_option_abbreviation", e.target.value)
                                                                                    }
                                                                                    className="form-control"
                                                                                    placeholder=""
                                                                                />
                                                                                {fieldErrors[row.id]?.sku_preset_option_abbreviation && (
                                                                                    <div style={{ color: '#F04438', fontSize: '12px', marginTop: '2px' }}>
                                                                                        {fieldErrors[row.id].sku_preset_option_abbreviation}
                                                                                    </div>
                                                                                )}
                                                                            </td>

                                                                            {/* Remarks */}
                                                                            <td style={{ borderBottom: "none", verticalAlign: 'top' }}>
                                                                                <input
                                                                                    onChange={(e) =>
                                                                                        handleChange(row.id, "remarks", e.target.value)
                                                                                    }
                                                                                    className="form-control"
                                                                                    placeholder=""
                                                                                />
                                                                            </td>

                                                                            {/* Action Buttons */}
                                                                            <td style={{ borderBottom: "none", verticalAlign: 'top' }} className="text-center">
                                                                                <>
                                                                                    <div style={{ height: rowHeight }}>
                                                                                        <button
                                                                                            className="icon-btn shadow-none text-gray"
                                                                                            onClick={() => handleSave(row.id)}
                                                                                        >
                                                                                            <Check size={18} />
                                                                                        </button>
                                                                                        <button
                                                                                            className="icon-btn shadow-none text-gray"
                                                                                            onClick={() => handleCancel(row.id)}
                                                                                        >
                                                                                            <X size={18} />
                                                                                        </button>
                                                                                    </div>
                                                                                </>
                                                                            </td>
                                                                        </tr>
                                                                    )
                                                                })}
                                                                {filteredAbbreviations.map((row, idx) => {

                                                                    const rowHeight = 45;
                                                                    const rowBgColor = idx % 2 === 0 ? '#FFFFFF' : '#F8F9FA';

                                                                    return (
                                                                        <tr key={row.idx} className={[
                                                                            idx % 2 ? 'striped' : '',
                                                                        ].join(' ')}
                                                                            style={{ height: '45px', backgroundColor: rowBgColor }}>
                                                                            <td>
                                                                                {/* FIX 6: Updated delete button to use new handlers */}
                                                                                <Trash2 
                                                                                    size={16} 
                                                                                    style={{ cursor: "pointer", color: "black" }} 
                                                                                    onClick={() => {
                                                                                        setDeleteId(row.sku_preset_option_id);
                                                                                        setShowDeleteModal(true);
                                                                                    }}
                                                                                />
                                                                            </td>
                                                                            {/* SKU Variation */}
                                                                            <td className={fieldErrors[row.id]?.sku_preset_id ? 'field-error' : ''} style={{ alignItems: "center", gap: 8, verticalAlign: 'top', overflow: 'hidden' }}> {/* Bin icon */}
                                                                                {editingAbbreviationId.includes(row.id)
                                                                                    ? (<>
                                                                                        <PortalDropdown
                                                                                            value={row.sku_preset_id}
                                                                                            placeholder="Select Preset"
                                                                                            options={
                                                                                                skuAttributes.map(attr => ({
                                                                                                    value: attr.sku_preset_id,
                                                                                                    label: attr.sku_preset_name,
                                                                                                }))
                                                                                            }
                                                                                            onChange={(val) =>
                                                                                                handleChange(row.id, "sku_preset_id", val, "edit")
                                                                                            }
                                                                                        />

                                                                                        {fieldErrors[row.id]?.sku_preset_id && (
                                                                                            <div style={{ color: '#F04438', fontSize: '12px', marginTop: '2px' }}>
                                                                                                {fieldErrors[row.id].sku_preset_id}
                                                                                            </div>
                                                                                        )}
                                                                                    </>) : (
                                                                                        <div style={{ height: rowHeight, display: 'flex', alignItems: 'center', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                                                            {skuAttributes.find((r) => r.sku_preset_id == row.sku_preset_id)?.sku_preset_name ?? null}
                                                                                        </div>
                                                                                    )}
                                                                            </td>

                                                                            {/* Variation Option */}
                                                                            <td className={'bg_yellow_option ' + (fieldErrors[row.id]?.sku_preset_option_id ? 'field-error' : '')} style={{ verticalAlign: 'top', overflow: 'hidden' }} >
                                                                                {editingAbbreviationId.includes(row.id)
                                                                                    ? (
                                                                                        <>
                                                                                            <PortalDropdown
                                                                                                value={row.sku_preset_option_id}
                                                                                                placeholder="Select Option"
                                                                                                options={
                                                                                                    skuVariationOptions[row.id]?.options.map(option => ({
                                                                                                        value: option.sku_preset_option_id,
                                                                                                        label: option.sku_preset_option_name,
                                                                                                    })) || []
                                                                                                }
                                                                                                onChange={(val) =>
                                                                                                    handleChange(row.id, "sku_preset_option_id", val, "edit")
                                                                                                }
                                                                                                fieldName="variation_option"
                                                                                            />

                                                                                            {fieldErrors[row.id]?.sku_preset_option_id && (
                                                                                                <div style={{ color: '#F04438', fontSize: '12px', marginTop: '2px' }}>
                                                                                                    {fieldErrors[row.id].sku_preset_option_id}
                                                                                                </div>
                                                                                            )}
                                                                                        </>
                                                                                    ) : (
                                                                                        <div style={{ height: rowHeight, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                                                                                            <span className="product-type-badge" style={{ flex: 'none', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                                                {row.sku_preset_option_name}
                                                                                            </span>
                                                                                        </div>

                                                                                    )}
                                                                            </td>

                                                                            {/* Case Sensitive */}
                                                                            <td style={{ verticalAlign: 'top' }}
                                                                            className="bg_yellow_option" data-text={row.is_case_sensitive}
                                                                            >
                                                                                {editingAbbreviationId.includes(row.id) ? (
                                                                                    <PortalDropdown
                                                                                        value={row.is_case_sensitive ?? 'no'}
                                                                                        options={
                                                                                            [
                                                                                                {'value': 'yes','label': 'YES'},
                                                                                                {'value': 'no','label': 'NO'},
                                                                                            ]
                                                                                        }
                                                                                        onChange={(val) =>
                                                                                            handleChange(row.id, "is_case_sensitive", val, 'edit')
                                                                                        }
                                                                                    />
                                                                                    
                                                                                ) : (
                                                                                    <div style={{ height: rowHeight, display: 'flex', alignItems: 'center' }}>
                                                                                        <span
                                                                                            style={{
                                                                                                display: "inline-flex",
                                                                                                height: "20px",
                                                                                                width: "72px",                // FIXED WIDTH — keeps badge consistent
                                                                                                padding: "5px 10px",
                                                                                                justifyContent: "center",
                                                                                                alignItems: "center",
                                                                                                borderRadius: "99px",
                                                                                                border:
                                                                                                    row.is_case_sensitive?.toUpperCase() === "YES"
                                                                                                        ? "1px solid #198754"
                                                                                                        : "1px solid #DC3545",
                                                                                                background:
                                                                                                    row.is_case_sensitive?.toUpperCase() === "YES"
                                                                                                        ? "#D1E7DD"
                                                                                                        : "#F8D7DA",
                                                                                                color:
                                                                                                    row.is_case_sensitive?.toUpperCase() === "YES"
                                                                                                        ? "#198754"
                                                                                                        : "#DC3545",
                                                                                                fontWeight: 600,
                                                                                                textTransform: "uppercase",
                                                                                                fontSize: "12px",
                                                                                                lineHeight: "normal",
                                                                                                fontFamily: "Montserrat",
                                                                                                fontStyle: "normal",
                                                                                            }}
                                                                                        >
                                                                                            {row.is_case_sensitive?.toUpperCase()}
                                                                                        </span>
                                                                                    </div>

                                                                                )}
                                                                            </td>




                                                                            {/* Abbreviation */}
                                                                            < td className={fieldErrors[row.id]?.sku_preset_option_abbreviation ? 'field-error' : ''} style={{ verticalAlign: 'top', overflow: 'hidden' }}>
                                                                                {editingAbbreviationId.includes(row.id) ? (
                                                                                    <><input
                                                                                        value={row.sku_preset_option_abbreviation}
                                                                                        onChange={(e) =>
                                                                                            handleChange(row.id, "sku_preset_option_abbreviation", e.target.value, 'edit')
                                                                                        }
                                                                                        className="form-control"
                                                                                        placeholder=""
                                                                                        style={{ border: "1px solid #666" }}
                                                                                    />
                                                                                        {fieldErrors[row.id]?.sku_preset_option_abbreviation && (
                                                                                            <div style={{ color: '#F04438', fontSize: '12px', marginTop: '2px' }}>
                                                                                                {fieldErrors[row.id].sku_preset_option_abbreviation}
                                                                                            </div>
                                                                                        )}</>
                                                                                ) : (
                                                                                    <div style={{ height: rowHeight, display: 'flex', alignItems: 'center', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                                                        {row.sku_preset_option_abbreviation}
                                                                                    </div>
                                                                                )}
                                                                            </td>

                                                                            {/* Remarks */}
                                                                            <td style={{ verticalAlign: 'top', overflow: 'hidden' }}>
                                                                                {editingAbbreviationId.includes(row.id) ? (
                                                                                    <input
                                                                                        value={row.remarks}
                                                                                        onChange={(e) =>
                                                                                            handleChange(row.id, "remarks", e.target.value, 'edit')
                                                                                        }
                                                                                        className="form-control"
                                                                                        placeholder=""
                                                                                        style={{ border: "1px solid #666" }}
                                                                                    />
                                                                                ) : (
                                                                                    <div style={{ height: rowHeight, display: 'flex', alignItems: 'center', fontFamily: "Montserrat", fontSize: "14px", fontStyle: 'normal', fontWeight: '400', color: "#333", overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                                                        {row.remarks?.trim() != '' ? row.remarks : <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                                    </div>
                                                                                )}
                                                                            </td>

                                                                            {/* Action Buttons */}
                                                                            <td style={{ textAlign: "center", verticalAlign: 'top' }}>
                                                                                {editingAbbreviationId.includes(row.id) ? (
                                                                                    <>
                                                                                        <div style={{ height: rowHeight }}>
                                                                                            <button
                                                                                                className="icon-btn shadow-none text-gray"
                                                                                                // onClick={saveEdit}
                                                                                                onClick={() => handleSave(row.id, 'edit')}
                                                                                            >
                                                                                                <Check size={18} />
                                                                                            </button>
                                                                                            <button
                                                                                                className="icon-btn shadow-none text-gray"
                                                                                                // onClick={cancelEdit}
                                                                                                onClick={() => handleCancel(row.id, 'edit')}
                                                                                            >
                                                                                                <X size={18} />
                                                                                            </button>
                                                                                        </div>
                                                                                    </>
                                                                                ) : (
                                                                                    <div style={{ height: rowHeight }}>
                                                                                        <button
                                                                                            className="icon-btn shadow-none text-gray"
                                                                                            onClick={() => startEditAbbrevation(row)}
                                                                                        >
                                                                                            <Pencil size={18} />
                                                                                        </button>
                                                                                    </div>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    )
                                                                })}
                                                            </tbody>


                                                        </table>
                                                    )
                                            )}
                                        {/* FIX 7: Updated DeleteConfirmModal with proper handlers */}
                                        <DeleteConfirmModal
                                            isOpen={showDeleteModal}
                                            entityName="Abbreviation"
                                            onCancel={handleDeleteCancel}
                                            onConfirm={handleDeleteConfirm}
                                            onClose={() => {
                                                setShowDeleteModal(false);
                                                setDeleteId(null);
                                                setIsDeleteOperation(false);
                                            }}
                                        />
                                    </div>
                                    <div className="pagination">
                                        {/* Info Text */}
                                        { filteredAbbreviations.length > 0 ?
                                            <div className="pagination-info" >
                                                {` 1 - ${filteredAbbreviations.length} of ${filteredAbbreviations.length} `}
                                            </div>
                                        : ''}
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                )
                }
            </div >

            <style>{`
            .col-resize-handle::after {
                content: "";
                position: absolute;
                top: 50%;
                transform: translateY(-50%);
                height: 50%;
                left: 3px;
                width: 2px;
                background: #DEE2E6;
            }
            .placeholder-dropdown-fixed > div:hover {
                background: #FFFEF0;
            }
  .product-type-badge {
    display: inline-flex;
    height: 20px;
    padding: 5px 10px;          /* smaller internal padding */
    margin: 2px 4px;           /* subtle space around the badge */
    justify-content: center;
    align-items: center;
    gap: 5px;

    border-radius: 99px;
    border: 1px solid var(--Primary-Color, #FFEB00);
    background: var(--Primary-Color, #FFEB00);

    font-family: Montserrat;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;

    width: fit-content;        /* auto-size */
    min-width: fit-content;
    flex: 1 0 0;
}

.field-error > div, .field-error > input {
    border-color: rgb(240, 68, 56) !important;
}

td.bg_yellow_option span:not(:empty), #portal-dropdown .option_variation_option {
    display: inline-flex;
    height: 20px;
    padding: 5px 10px;
    margin: 2px 4px;
    justify-content: center;
    align-items: center;
    gap: 5px;
    border-radius: 99px;
    border: 1px solid var(--Primary-Color, #FFEB00);
    background: var(--Primary-Color, #FFEB00);
    font-family: Montserrat;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    width: fit-content;
    min-width: fit-content;
}


td.bg_yellow_option[data-text='no'] span:not(:empty), td.bg_yellow_option[data-text='yes'] span:not(:empty) {
    display: inline-flex;
    height: 20px;
    width: 72px;
    padding: 5px 10px;
    justify-content: center;
    align-items: center;
    border-radius: 99px;
    color: rgb(220, 53, 69);
    font-weight: 600;
    text-transform: uppercase;
    font-size: 12px;
    line-height: normal;
    font-family: Montserrat;
    font-style: normal;
}
td.bg_yellow_option[data-text='no'] span:not(:empty) {
    border: 1px solid #DC3545;
    background: #F8D7DA;
    color: #DC3545 !important;
}
td.bg_yellow_option[data-text='yes'] span:not(:empty) {
    border: 1px solid #198754;
    background: #D1E7DD;
    color: #198754 !important;
}
    
#portal-dropdown .option-no {
    display: inline-flex;
    height: 20px;
    width: 72px;
    padding: 5px 10px;
    justify-content: center;
    align-items: center;
    border-radius: 99px;
    border: 1px solid rgb(220, 53, 69);
    background: rgb(248, 215, 218);
    color: rgb(220, 53, 69);
    font-weight: 600;
    text-transform: uppercase;
    font-size: 12px;
    line-height: normal;
    font-family: Montserrat;
    font-style: normal;
}
#portal-dropdown .option-yes {
    display: inline-flex;
    height: 20px;
    width: 72px;
    padding: 5px 10px;
    justify-content: center;
    align-items: center;
    border-radius: 99px;
    border: 1px solid rgb(25, 135, 84);
    background: rgb(209, 231, 221);
    color: rgb(25, 135, 84);
    font-weight: 600;
    text-transform: uppercase;
    font-size: 12px;
    line-height: normal;
    font-family: Montserrat;
    font-style: normal;
}

        .sku-scheme-text {
    font-family: Montserrat;
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    color: #5E6E82;
}

/* Dynamic placeholder colors */
.placeholder-BrandPrefix {
    color: #2C7BE5;
}

.placeholder-CategoryPrefix {
    color: #FF00BF;
}

.placeholder-SPUPrefix {
    color: #00FF37;
}

.placeholder-SKUAbbr {
    color: #FF8000;
}

.placeholder-OtherPref {
    color: var(--Gray-900, #FF8000);
}

    .example-text {
        font-family: Montserrat;
        font-size: 13px;
        font-style: normal;
        font-weight: 400;
        line-height: 20px;
        color:  #5E6E82;
    }

//   .placeholder-dropdown {
//     position: absolute;
//     top: 100%;
//     left: 0;
//     margin-top: 10px;
//     background: #FFFFFF;
//     border: 1px solid #D6D6D6;
//     border-radius: 16px;
//     padding: 20px 0px;
//     width: 260px;
//     z-index: 2000;
//   }

/* Default positioning (opens downward) */
.placeholder-dropdown {
    position: absolute;
    /* This makes it appear below the input */
top: 100%; /* Below the input/100% down from top of <td> */
    bottom: auto; /* Ensure this is not overriding top */    left: 0;
    z-index: 10; /* Ensure it's above other table elements */
    /* Other styling: background, border, etc. */
    background-color: white;
    border: 1px solid #ccc;
    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
    max-height: 250px; /* Example height for calculation */
    overflow-y: auto;
}

/* Positioning when it needs to open upward */
.placeholder-dropdown.open-upward {
    /* Set 'bottom' to 100% to position it above the input */
    /* 100% of the input's height (or parent <td>'s height) */
    bottom: 100%; 
    top: auto; /* Reset 'top' */
}

  .placeholder-item {
    padding: 6px 32px;
    font-family: Montserrat;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    display: block;
    white-space: nowrap;
  }

  .placeholder-item:hover {
    background: #F3F4F6; /* light grey hover like your screenshot */
  }

.ph-editor {
  display: block;
  white-space: nowrap !important;   /* ⭐ keeps everything in one line */
  overflow-x: auto;
  overflow-y: hidden;
}

.ph-editor span.phTag {
  display: inline-block; /* keeps tags aligned inline */
  white-space: nowrap;
  vertical-align: middle;
}
  /* New Fixed Positioning Class */


.form-control:focus {
    border-color: inherit !important;   /* keeps your red or grey border */
    box-shadow: none !important;        /* removes the blue glow */
    outline: none !important;           /* removes default outline */
}
.placeholder-dropdown-fixed {
    position: absolute;
    z-index: 99999 !important;   /* always above table */
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 8px;
    box-shadow: 0 4px 14px rgba(0,0,0,0.15);

    /* VERY IMPORTANT */
    max-height: 300px;            /* allow dropdown to grow */
    overflow-y: auto;             /* internal scroll only if too tall */
    
    /* Ensure it does not clip inside any parent */
    pointer-events: auto;
}


  `}</style>
        </div >
    );
}